// Updated CompetitionControl.js
import React, { useState, useEffect } from 'react';
import './CompetitionControl.css';
import BackgroundManager from './BackgroundManager';

//const _host = 'chappie-demo.novosense.africa';
const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const CompetitionControl = ({ event, heats, onNextHeat, onPreviousHeat, onStopCompetition, onUpdateBackground }) => {
  const { currentDanceIndex = 0, currentHeatIndex = 0 } = event || {};

  // Safety checks for heats and indices
  const hasValidHeats = heats && Array.isArray(heats) && heats.length > 0;
  const currentDance = hasValidHeats && heats[currentDanceIndex] ? heats[currentDanceIndex] : null;
  const currentHeatData = currentDance?.heats && currentDance.heats[currentHeatIndex] ? currentDance.heats[currentHeatIndex] : null;

  const [showBackgroundManager, setShowBackgroundManager] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [showIdleDisplay, setShowIdleDisplay] = useState(false);

  // Calculate navigation states with safety checks
  const isLastHeat = currentDance?.heats ? currentHeatIndex >= currentDance.heats.length - 1 : true;
  const isLastDance = hasValidHeats ? currentDanceIndex >= heats.length - 1 : true;
  const isLastHeatOfLastDance = isLastHeat && isLastDance;

  const isFirstHeat = currentHeatIndex <= 0;
  const isFirstDance = currentDanceIndex <= 0;
  const isFirstHeatOfFirstDance = isFirstHeat && isFirstDance;
  
  // Load current background and display settings on initial render
  useEffect(() => {
    const fetchCurrentSettings = async () => {
      try {
        const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');
        if (response.ok) {
          const data = await response.json();
          if (data) {
            if (data.backgroundId) {
              setSelectedBackground({ id: data.backgroundId });
            }
            if (data.showIdleDisplay !== undefined) {
              setShowIdleDisplay(data.showIdleDisplay);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching current settings:', error);
      }
    };
    
    fetchCurrentSettings();
  }, []);
  
  // Get next heat info for the button label
  let nextButtonLabel = 'Next Heat';
  
  if (isLastHeatOfLastDance) {
    nextButtonLabel = 'Complete Competition';
  } else if (isLastHeat && hasValidHeats && heats[currentDanceIndex + 1]) {
    const nextDance = heats[currentDanceIndex + 1];
    nextButtonLabel = `Next Dance: ${nextDance.dance || 'Unknown'}, Heat 1`;
  } else if (currentDance) {
    nextButtonLabel = `Next Heat: ${currentDance.dance || 'Unknown'}, Heat ${currentHeatIndex + 2}`;
  } else {
    nextButtonLabel = 'Next Heat';
  }
  
  // Get previous heat info for the button label
  let previousButtonLabel = 'Previous Heat';
  
  if (isFirstHeatOfFirstDance) {
    previousButtonLabel = 'Back to Start';
  } else if (isFirstHeat && hasValidHeats && heats[currentDanceIndex - 1]) {
    const previousDance = heats[currentDanceIndex - 1];
    const lastHeatIndex = previousDance.heats ? previousDance.heats.length - 1 : 0;
    previousButtonLabel = `Previous Dance: ${previousDance.dance || 'Unknown'}, Heat ${lastHeatIndex + 1}`;
  } else if (currentDance) {
    previousButtonLabel = `Previous Heat: ${currentDance.dance || 'Unknown'}, Heat ${currentHeatIndex}`;
  } else {
    previousButtonLabel = 'Previous Heat';
  }
  
  // Handle background selection
  const handleBackgroundSelected = async (background) => {
    setSelectedBackground(background);
    
    if (onUpdateBackground) {
      onUpdateBackground(background);
    } else {
      // If no callback is provided, update the display directly
      try {
        const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');
        if (response.ok) {
          const displayData = await response.json();
          
          // Update with new background
          const updateData = {
            ...displayData,
            backgroundId: background ? background.id : null,
            // Preserve idle display setting
            showIdleDisplay: showIdleDisplay
          };
          
          await fetch('https://'+_host+':6003/api/'+_company+'/display', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          });
        }
      } catch (error) {
        console.error('Error updating background:', error);
      }
    }
  };
  
  // Handle idle display toggle
  const handleToggleIdleDisplay = async (value) => {
    setShowIdleDisplay(value);
    
    try {
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');
      if (response.ok) {
        const displayData = await response.json();
        
        // Update with new setting
        const updateData = {
          ...displayData,
          showIdleDisplay: value,
          // Preserve background setting
          backgroundId: selectedBackground ? selectedBackground.id : null
        };
        
        await fetch('https://'+_host+':6003/api/'+_company+'/display', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });
      }
    } catch (error) {
      console.error('Error updating idle display setting:', error);
    }
  };
  
  if (!hasValidHeats || !currentDance || !currentHeatData) {
    return (
      <div className="competition-control error">
        <h3>Competition Not Ready</h3>
        <p>{!hasValidHeats ?
          'No heats have been generated for this event. Please generate heats first.' :
          'Invalid heat data. Please check the event configuration.'}
        </p>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowBackgroundManager(!showBackgroundManager)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <i className="fas fa-cog text-xl"></i>
              </button>
              <button
                onClick={onStopCompetition}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <i className="fas fa-stop-circle text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Competition Status Card */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-2">{event.name.toUpperCase()}</h2>
                <div className="flex items-center space-x-4">
                  <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
                    <i className="fas fa-circle text-xs mr-2 animate-pulse"></i>
                    LIVE
                  </span>
                  <span className="text-indigo-100">{event.roundType}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">00:00</div>
                <div className="text-sm text-indigo-100">HEAT {currentHeatIndex + 1} OF {currentDance.heats.length}</div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Current Heat Info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Current Heat</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xl font-bold text-gray-900">{currentDance.dance}</div>
                    <div className="text-sm text-gray-600">HEAT {currentHeatData.heatNumber}</div>
                  </div>
                </div>

                {/* Competing Couples */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Dancers on Floor</h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {currentHeatData.dancers
                      .sort((a, b) => parseInt(a.clothNumber) - parseInt(b.clothNumber))
                      .map((dancer) => (
                        <div key={dancer.clothNumber} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-indigo-200 rounded-lg p-4 text-center cursor-pointer hover:scale-105 transition-transform">
                          <div className="text-xl font-bold text-indigo-600">{dancer.clothNumber}</div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Next Up Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Next Up</h3>
                {!isLastHeatOfLastDance ? (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-xl font-bold text-gray-900">
                          {isLastHeat && !isLastDance ? heats[currentDanceIndex + 1].dance : currentDance.dance}
                        </div>
                        <div className="text-sm text-gray-600">
                          {isLastHeat && !isLastDance ? "HEAT 1" : `HEAT ${currentHeatIndex + 2}`}
                        </div>
                      </div>
                      <i className="fas fa-clock text-green-600 text-2xl"></i>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                        {currentHeatData.dancers.length} Couples
                      </span>
                      {isLastHeat && !isLastDance && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          New Dance
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex gap-2">
                      {currentDance.heats[currentHeatIndex + 1] && !isLastHeat && (
                        <div className="flex flex-wrap gap-1">
                          {currentDance.heats[currentHeatIndex + 1].dancers
                            .sort((a, b) => parseInt(a.clothNumber) - parseInt(b.clothNumber))
                            .map(dancer => (
                              <span key={dancer.clothNumber} className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">
                                {dancer.clothNumber}
                              </span>
                            ))}
                        </div>
                      )}
                      {isLastHeat && !isLastDance && (
                        <div className="flex flex-wrap gap-1">
                          {heats[currentDanceIndex + 1].heats[0].dancers
                            .sort((a, b) => parseInt(a.clothNumber) - parseInt(b.clothNumber))
                            .map(dancer => (
                              <span key={dancer.clothNumber} className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">
                                {dancer.clothNumber}
                              </span>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-orange-200 rounded-lg p-6 text-center">
                    <span className="text-4xl mb-2">🏆</span>
                    <h3 className="text-xl font-bold text-gray-900">Final Heat</h3>
                    <p className="text-gray-600">Complete competition after this heat</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md mb-6 p-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowBackgroundManager(!showBackgroundManager)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors"
            >
              <i className="fas fa-image"></i>
              <span>Background</span>
            </button>
            <button
              onClick={onStopCompetition}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors"
            >
              <i className="fas fa-stop"></i>
              <span>{event.active ? 'Stop Competition' : 'Start Competition'}</span>
            </button>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center">
            <button
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={onPreviousHeat}
              disabled={isFirstHeatOfFirstDance}
            >
              <i className="fas fa-chevron-left"></i>
              <div>
                <div className="font-medium">{previousButtonLabel}</div>
                <div className="text-xs opacity-75">Previous</div>
              </div>
            </button>
            <button
              className={`${
                isLastHeatOfLastDance
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              } text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors`}
              onClick={onNextHeat}
            >
              <div>
                <div className="font-medium">{nextButtonLabel}</div>
                <div className="text-xs opacity-75">
                  {isLastHeatOfLastDance ? 'Complete' : 'Next'}
                </div>
              </div>
              <i className={isLastHeatOfLastDance ? 'fas fa-check-flag' : 'fas fa-chevron-right'}></i>
            </button>
          </div>
        </div>
      </main>

      {/* Background Manager Modal */}
      {showBackgroundManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Display Settings</h3>
              <button
                onClick={() => setShowBackgroundManager(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-6">
              <BackgroundManager
                onBackgroundSelected={handleBackgroundSelected}
                currentBackground={selectedBackground}
                onToggleIdleDisplay={handleToggleIdleDisplay}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionControl;